import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOktaAuth } from '@okta/okta-react';
import { ArrowLeft, Send, Loader2, Sparkles, Paperclip, AlertCircle } from 'lucide-react';
import { apiConfig } from '../../../config';
import type { WorkbenchSession, WorkbenchMessage, MessageContentBlock } from '../../../types';
import PromptTemplatesModal from './PromptTemplatesModal';
import type { AgentSpec } from './types';

/**
 * ONE chat implementation for every agent. All streaming / SSE / state
 * logic is the proven profiling-agent logic, UNCHANGED — only the
 * per-agent bits (api, endpoint, title, message renderer, detail-card
 * tool set, sample prompts) come from `spec`. profiling, incident and
 * data-content-profiler all render through this — no duplication.
 */
const AgentConversation: React.FC<{ spec: AgentSpec }> = ({ spec }) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { oktaAuth } = useOktaAuth();
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>('');
  // Persistent prompt-reference panel — available the ENTIRE time the
  // user is interacting (toggle near the input), not a one-time hint.
  const [showPrompts, setShowPrompts] = useState(false);

  const [liveMessages, setLiveMessages] = useState<WorkbenchMessage[] | null>(null);
  const [liveArtifacts, setLiveArtifacts] = useState<Record<string, any> | null>(null);
  const [statusBanner, setStatusBanner] = useState<string>('');
  const [runningTool, setRunningTool] = useState<string>('');

  // An attached file is held HERE (read client-side) and shown as a
  // compact chip — NOT dumped into the textarea. It is appended to the
  // outgoing message on send. Gated by spec.allowFileAttach so other
  // agents are unaffected (this stays null for them).
  const [attachment, setAttachment] = useState<{ name: string; content: string } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAttachFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '').trim();
      if (!text) return;
      setAttachment({ name: file.name, content: text });
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  useEffect(() => {
    setLiveMessages(null);
    setLiveArtifacts(null);
  }, [sessionId]);

  const { data, refetch } = useQuery({
    queryKey: [spec.queryKeyPrefix, 'session', sessionId],
    queryFn: async () => (await spec.api.getSession(sessionId!)).data.payload as WorkbenchSession,
    enabled: !!sessionId,
  });

  useEffect(() => {
    if (data?.artifacts && liveArtifacts === null) {
      setLiveArtifacts(data.artifacts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.artifacts]);

  const messages: WorkbenchMessage[] = liveMessages ?? data?.messages ?? [];
  const artifacts = liveArtifacts ?? data?.artifacts ?? {};

  const detailCardToolUseIds = React.useMemo(() => {
    const ids = new Set<string>();
    const knownDetail = new Set(spec.detailCardTools);
    for (const m of messages) {
      if (m.role !== 'assistant' || !Array.isArray(m.content)) continue;
      for (const b of m.content as MessageContentBlock[]) {
        if (b.type === 'tool_use' && knownDetail.has((b as any).name) && (b as any).id) {
          ids.add((b as any).id);
        }
      }
    }
    return ids;
  }, [messages, spec.detailCardTools]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, sending, statusBanner]);

  const appendToLastAssistant = (block: MessageContentBlock, setter: React.Dispatch<React.SetStateAction<WorkbenchMessage[]>>) => {
    setter((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant' && Array.isArray(last.content)) {
        next[next.length - 1] = { ...last, content: [...(last.content as MessageContentBlock[]), block] };
        return next;
      }
      next.push({ role: 'assistant', content: [block], at: new Date().toISOString() });
      return next;
    });
  };

  const appendTextDelta = (
    chunk: string,
    setter: React.Dispatch<React.SetStateAction<WorkbenchMessage[]>>,
  ) => {
    if (!chunk) return;
    setter((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant' && Array.isArray(last.content)) {
        const content = [...(last.content as MessageContentBlock[])];
        const tail = content[content.length - 1] as any;
        if (tail && tail.type === 'text') {
          content[content.length - 1] = { ...tail, text: (tail.text || '') + chunk };
        } else {
          content.push({ type: 'text', text: chunk } as MessageContentBlock);
        }
        next[next.length - 1] = { ...last, content };
        return next;
      }
      next.push({
        role: 'assistant',
        content: [{ type: 'text', text: chunk } as MessageContentBlock],
        at: new Date().toISOString(),
      });
      return next;
    });
  };

  const appendToolResult = (
    block: MessageContentBlock,
    setter: React.Dispatch<React.SetStateAction<WorkbenchMessage[]>>,
  ) => {
    setter((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'user' && last._kind === 'tool_results' && Array.isArray(last.content)) {
        next[next.length - 1] = {
          ...last,
          content: [...(last.content as MessageContentBlock[]), block],
        };
        return next;
      }
      next.push({
        role: 'user',
        content: [block],
        at: new Date().toISOString(),
        _kind: 'tool_results',
      });
      return next;
    });
  };

  const handleSend = async (override?: string) => {
    const typed = (override ?? input).trim();
    // The attached file rides along with the message (same payload as
    // pasting it) — appended here, never shown raw in the textarea.
    const attachBlock = attachment
      ? `${typed ? '\n\n' : ''}Attached file "${attachment.name}":\n`
        + `\`\`\`json\n${attachment.content}\n\`\`\``
      : '';
    const text = (typed + attachBlock).trim();
    if (!text || sending) return;
    setError('');
    setSending(true);
    setInput('');
    setAttachment(null);
    setStatusBanner('');
    setRunningTool('');

    const baseHistory = liveMessages ?? data?.messages ?? [];
    const seeded: WorkbenchMessage[] = [
      ...baseHistory,
      { role: 'user', content: text, at: new Date().toISOString() },
    ];
    setLiveMessages(seeded);
    const buffer = { msgs: seeded };
    const setter: React.Dispatch<React.SetStateAction<WorkbenchMessage[]>> = (upd) => {
      buffer.msgs = typeof upd === 'function' ? upd(buffer.msgs) : upd;
      setLiveMessages(buffer.msgs);
    };

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url = `${apiConfig.baseUrl.replace(/\/$/, '')}${spec.streamUrlBase}/sessions/${sessionId}/messages/stream`;
      const token = oktaAuth?.getAccessToken?.();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content: text }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });

        let boundary;
        while ((boundary = sseBuffer.indexOf('\n\n')) !== -1) {
          const rawEvent = sseBuffer.slice(0, boundary);
          sseBuffer = sseBuffer.slice(boundary + 2);
          const evt = parseSSE(rawEvent);
          if (evt) handleEvent(evt, setter);
        }
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        return;
      }
      setError(e.message || 'Stream failed');
      setInput(text);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setSending(false);
      setStatusBanner('');
      setRunningTool('');
      try {
        await refetch();
      } catch {
        /* ignore — we still have buffer.msgs + liveArtifacts for display */
      }
      setLiveMessages(buffer.msgs);
    }
  };

  const handleEvent = (
    evt: { type: string; data: any },
    setter: React.Dispatch<React.SetStateAction<WorkbenchMessage[]>>,
  ) => {
    switch (evt.type) {
      case 'status':
        setStatusBanner(evt.data?.message ?? '');
        break;
      case 'user_message':
        break;
      case 'assistant_text':
        setStatusBanner('');
        appendToLastAssistant({ type: 'text', text: evt.data?.text ?? '' }, setter);
        break;
      case 'assistant_text_delta':
        setStatusBanner('');
        appendTextDelta(evt.data?.text ?? '', setter);
        break;
      case 'tool_use':
        setStatusBanner('');
        appendToLastAssistant(
          { type: 'tool_use', id: evt.data?.id, name: evt.data?.name, input: evt.data?.input ?? {} },
          setter,
        );
        break;
      case 'tool_running':
        setRunningTool(evt.data?.name ?? '');
        break;
      case 'tool_result':
        setRunningTool('');
        appendToolResult(
          { type: 'tool_result', tool_use_id: evt.data?.id, content: evt.data?.content ?? '' },
          setter,
        );
        break;
      case 'artifacts':
        if (evt.data?.artifacts && typeof evt.data.artifacts === 'object') {
          setLiveArtifacts(evt.data.artifacts);
        }
        break;
      case 'error':
        setError(evt.data?.error ?? 'Unknown error');
        break;
      case 'done':
      case 'stream_end':
        setStatusBanner('');
        setRunningTool('');
        break;
      default:
        break;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const Bubble = spec.MessageBubble;

  return (
    <div className="h-[calc(100vh-5rem)] max-w-5xl mx-auto flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => navigate(spec.backRoute)}
          className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors"
          title="Back to sessions"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-cargo-text flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cargo-accent" />
            {data?.title || spec.title}
          </h1>
          <p className="text-cargo-muted text-xs font-mono mt-0.5">{sessionId}</p>
        </div>
        {Object.keys(artifacts).length > 0 && (
          <div className="flex items-center gap-1.5 text-cargo-muted text-xs">
            <Paperclip className="w-3.5 h-3.5" />
            <span>{Object.keys(artifacts).length} artifact(s)</span>
          </div>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-6 bg-cargo-panel/30 border border-cargo-border rounded-lg"
      >
        {!!data && messages.length === 0 && (
          <div className="max-w-md mx-auto text-center py-20">
            <Sparkles className="w-10 h-10 mx-auto mb-4 text-cargo-accent opacity-70" />
            <h2 className="text-cargo-text text-lg font-semibold mb-1.5">{spec.title}</h2>
            <p className="text-cargo-muted text-sm">{spec.subtitle}</p>
          </div>
        )}

        {messages.map((m, i) => (
          <Bubble
            key={i}
            message={m}
            artifacts={artifacts}
            detailCardToolUseIds={detailCardToolUseIds}
          />
        ))}

        {/* Optional per-agent trailing panel (e.g. the Schema Mapping
            result card) — rendered ONCE at the end so it sits with the
            final summary, not stranded at an early tool call. Other
            agents don't set spec.TrailingPanel → nothing renders. */}
        {spec.TrailingPanel && <spec.TrailingPanel artifacts={artifacts} />}

        {sending && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-cargo-accent text-white flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="text-cargo-muted text-[11px] uppercase tracking-wider mb-1">
                {spec.title}
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-cargo-panel border border-cargo-border text-cargo-muted text-sm">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {runningTool ? (
                  <>
                    Running <span className="font-mono text-cargo-accent">{runningTool}</span>…
                  </>
                ) : statusBanner ? (
                  statusBanner
                ) : (
                  'Working…'
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 bg-cargo-error/10 border border-cargo-error/30 rounded-lg px-3 py-2 text-cargo-error text-xs flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Input */}
      <div className="mt-4">
        {/* Prompt-templates POPUP trigger — open any time while
            interacting; it's a format reference. */}
        {spec.samplePrompts.length > 0 && (
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setShowPrompts(true)}
              className="inline-flex items-center gap-1.5 text-xs text-cargo-muted hover:text-cargo-text transition-colors"
              title="Open prompt templates (format reference)"
            >
              <Sparkles className="w-3.5 h-3.5 text-cargo-accent" />
              <span>Prompt templates</span>
            </button>
          </div>
        )}
        {spec.allowFileAttach && attachment && (
          <div className="mb-2 inline-flex items-center gap-2 bg-cargo-panel border border-cargo-border rounded-lg px-3 py-1.5 text-xs">
            <Paperclip className="w-3.5 h-3.5 text-cargo-accent flex-shrink-0" />
            <span className="text-cargo-text font-mono truncate max-w-[260px]">{attachment.name}</span>
            <span className="text-cargo-muted">attached</span>
            <button
              type="button"
              onClick={() => setAttachment(null)}
              disabled={sending}
              className="text-cargo-muted hover:text-cargo-error transition-colors disabled:opacity-50"
              title="Remove attachment"
            >
              ✕
            </button>
          </div>
        )}
        <PromptTemplatesModal
          open={showPrompts}
          onClose={() => setShowPrompts(false)}
          prompts={spec.samplePrompts}
          onPick={(p) => setInput(p)}
        />
        <div className="relative">
          {spec.allowFileAttach && (
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json,.txt,text/plain"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleAttachFile(e.target.files[0]);
                e.target.value = '';
              }}
            />
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={spec.placeholder}
            rows={spec.allowFileAttach ? 1 : 2}
            disabled={sending}
            className={`w-full resize-none py-3 pr-12 bg-cargo-panel border border-cargo-border rounded-lg text-cargo-text text-sm focus:outline-none focus:ring-2 focus:ring-cargo-accent disabled:opacity-60 ${spec.allowFileAttach ? 'pl-11' : 'pl-4'}`}
          />
          {spec.allowFileAttach && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              className="absolute top-1/2 -translate-y-1/2 left-2.5 p-2 text-cargo-muted hover:text-cargo-text rounded-md hover:bg-cargo-border/40 disabled:opacity-40 transition-colors"
              title="Attach a schema JSON file (read in your browser, not uploaded)"
            >
              <Paperclip className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => handleSend()}
            disabled={(!input.trim() && !attachment) || sending}
            className={`absolute right-2.5 p-2 bg-cargo-accent text-white rounded-md hover:bg-cargo-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
              spec.allowFileAttach ? 'top-1/2 -translate-y-1/2' : 'bottom-2.5'
            }`}
            title="Send (Enter)"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-cargo-muted text-[10px] mt-1.5 px-1">
          Enter to send  ·  Shift + Enter for new line
        </p>
      </div>
    </div>
  );
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Parse a single SSE event block: "event: X\ndata: Y" */
function parseSSE(raw: string): { type: string; data: any } | null {
  if (!raw.trim()) return null;
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }
  const dataStr = dataLines.join('\n');
  if (!dataStr) return { type: eventName, data: {} };
  try {
    return { type: eventName, data: JSON.parse(dataStr) };
  } catch {
    return { type: eventName, data: { raw: dataStr } };
  }
}

export default AgentConversation;
